import {
  Box,
  Typography,
  TableCell,
  TableRow,
  CircularProgress,
  Stack,
  Paper,
  Skeleton,
  useMediaQuery,
  Theme,
} from "@mui/material"
import { useStore } from "@nanostores/react"
import { useEffect, useMemo, useState } from "react"

import { Pool } from "@/api/pools-api"
import { useLpBalance, useTokenPrices } from "@/api/portfolio-api"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { IdBlock } from "@/components/IdBlock"
import { InMemoryTable } from "@/components/InMemoryTable"
import { LoadingSkeletons } from "@/components/LoadingSkeletons"
import { NumberFont } from "@/components/RootLayout/fonts"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { TokenAvatar } from "@/components/TokenAvatar"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"
import { useTokenInfo } from "@/hooks/use-token-info"
import { useReserves } from "@/hooks/usePoolReserves"
import { useTotalSupply } from "@/hooks/useTotalSupply"
import { $allPools } from "@/stores/pools-store"
import { truncateId } from "@/utils/data-utils"
import { parseBigIntAsNumber } from "@/utils/format"

export function EntityLP(props: { entityId: string; onLpValue?: (value: number | null) => void }) {
  const { entityId, onLpValue } = props
  const pools = useStore($allPools)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  const [values, setValues] = useState<Record<string, number>>({})

  const totalValue = useMemo(() => {
    if (!pools) return null
    const val = Object.values(values || {}).reduce((acc, v) => acc + v, 0)
    onLpValue?.(val)
    return val
  }, [pools, values, onLpValue])

  useEffect(() => {
    setValues({})
  }, [entityId])

  return (
    <Box>
      <Typography
        sx={{
          marginTop: 2,
          marginBottom: 1,
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
        variant="h4"
      >
        Pools{" "}
        {!!totalValue && (
          <>
            •{" "}
            <Typography fontFamily={NumberFont} variant="inherit" component="span">
              <UsdAmountBlock amount={totalValue} />
            </Typography>
          </>
        )}
      </Typography>
      {pools.length === 0 ? (
        <LoadingSkeletons />
      ) : (
        <Paper sx={{ border: 0, paddingX: 2, paddingY: 2 }}>
          {isMobile ? (
            <Stack>
              {pools.map((pool, index) => (
                <ErrorBoundary key={pool.id} fallback={<>Something went wrong</>}>
                  <PoolRowMobile
                    pool={pool}
                    entityId={entityId}
                    onLpValue={(value) => {
                      setValues((prev) => ({ ...prev, [pool.id]: value }))
                    }}
                    index={index}
                  />
                </ErrorBoundary>
              ))}
            </Stack>
          ) : (
            <InMemoryTable
              initialSortField="netWorth"
              initialSortDir="desc"
              data={pools}
              pageSize={pools.length}
              headerCells={
                totalValue
                  ? [
                      { label: "Pool" },
                      { label: "Token 1", align: "right" },
                      { label: "Token 2", align: "right" },
                      { label: "Total Value", align: "right" },
                    ]
                  : []
              }
              renderRow={(pool, index) => (
                <ErrorBoundary key={pool.id} fallback={<>Something went wrong</>}>
                  <PoolRow
                    index={index}
                    pool={pool}
                    entityId={entityId}
                    onLpValue={(value: number) => {
                      setValues((prev = {}) => {
                        console.log(
                          "onLpPosition",
                          pool.id,
                          value,
                          "onLpProgress",
                          Object.keys(prev || {}).length + 1,
                          "out of",
                          pools.length,
                          // "missing pools",
                          // pools.filter((p) => prev[p.id] === undefined).map((x) => x.id),
                        )

                        return { ...prev, [pool.id]: value }
                      })
                    }}
                  />
                </ErrorBoundary>
              )}
            />
          )}
          {Object.keys(values || {}).length < pools.length && (
            <Stack paddingY={1.5} paddingX={2} direction="row" gap={1} alignItems="center">
              <CircularProgress size={12} color="primary" />
              <Typography variant="body2" color="text.secondary">
                Checking more pools... ({Object.keys(values || {}).length}/{pools.length})
              </Typography>
            </Stack>
          )}
          {pools.length > 0 &&
            Object.keys(values || {}).length === pools.length &&
            totalValue === 0 && (
              <Typography sx={{ marginX: 2, marginY: 1 }} variant="body2" color="text.secondary">
                No LP positions found.
              </Typography>
            )}
        </Paper>
      )}
    </Box>
  )
}

function PoolRow(props: {
  pool: Pool
  entityId: string
  onLpValue: (value: number) => void
  index: number
}) {
  const { pool, entityId, onLpValue, index } = props

  const lpBalance = useLpBalance(entityId, pool.id)

  const [reserves] = useReserves(pool.id, pool.baseToken, pool.quoteToken)
  const [lpTotalSupply] = useTotalSupply(pool.id)
  const [baseToken] = useTokenInfo(pool.baseToken)
  const [quoteToken] = useTokenInfo(pool.quoteToken)
  const [tokenPrices] = useTokenPrices()

  const isLoading =
    typeof lpBalance !== "bigint" ||
    !reserves ||
    !tokenPrices ||
    !baseToken ||
    !quoteToken ||
    typeof lpTotalSupply !== "bigint"

  const baseTokenValue = useMemo(() => {
    if (isLoading) return null
    if (lpBalance === 0n || lpTotalSupply === 0n) return 0
    const price = tokenPrices[baseToken.id]?.price
    const balance = (reserves[baseToken.id] * lpBalance) / lpTotalSupply
    const value =
      (price || 0) *
      Number(parseBigIntAsNumber(balance, baseToken.denomination, baseToken.denomination))

    return value
  }, [isLoading, tokenPrices, baseToken, reserves, lpBalance, lpTotalSupply])

  const quoteTokenValue = useMemo(() => {
    if (isLoading) return null
    if (lpBalance === 0n || lpTotalSupply === 0n) return 0
    const price = tokenPrices[quoteToken.id]?.price
    const balance = (reserves[quoteToken.id] * lpBalance) / lpTotalSupply
    const value =
      (price || 0) *
      Number(parseBigIntAsNumber(balance, quoteToken.denomination, quoteToken.denomination))

    return value
  }, [isLoading, tokenPrices, quoteToken, reserves, lpBalance, lpTotalSupply])

  const total = useMemo(() => {
    if (isLoading || typeof baseTokenValue !== "number" || typeof quoteTokenValue !== "number")
      return null

    const val = baseTokenValue + quoteTokenValue
    onLpValue(val)
    return val
  }, [isLoading, baseTokenValue, quoteTokenValue])

  const [priceQuote, balanceQuote, valueQuote] = useMemo(() => {
    if (isLoading || lpBalance === 0n) return [null, null, null]

    const price = tokenPrices[quoteToken.id]?.price
    let balance = 0n
    try {
      balance = (reserves[quoteToken.id] * lpBalance) / lpTotalSupply
    } catch {}
    const value =
      (price || 0) *
      Number(parseBigIntAsNumber(balance, quoteToken.denomination, quoteToken.denomination))

    return [price, balance, value]
  }, [tokenPrices, quoteToken, reserves, lpBalance, lpTotalSupply])

  const [priceBase, balanceBase, valueBase] = useMemo(() => {
    if (isLoading || lpBalance === 0n) return [null, null, null]

    const price = tokenPrices[baseToken.id]?.price
    let balance = 0n
    try {
      balance = (reserves[baseToken.id] * lpBalance) / lpTotalSupply
    } catch {}
    const value =
      (price || 0) *
      Number(parseBigIntAsNumber(balance, baseToken.denomination, baseToken.denomination))

    return [price, balance, value]
  }, [tokenPrices, baseToken, reserves, lpBalance, lpTotalSupply])

  if (isLoading || lpBalance === 0n) return null

  return (
    <TableRow
      sx={{
        background: "var(--mui-palette-paperAlt)",
        fontSize: "1rem",
        fontWeight: 700,
        "& > td": { border: 0, paddingTop: 2 },
      }}
    >
      {/* <TableCell align="right">{index + 1}</TableCell> */}
      <TableCell>
        <Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <TokenAvatar tokenId={quoteToken.id} size="medium" />
            <TokenAvatar tokenId={baseToken.id} size="medium" />
            <Typography variant="body1" fontWeight={700} marginLeft={1}>
              {quoteToken.ticker}/{baseToken.ticker}
            </Typography>
            <Typography variant="caption" marginLeft={6}>
              <IdBlock
                color="text.secondary"
                label={truncateId(pool.id)}
                value={pool.id}
                href={`/pool/${pool.id}`}
              />
            </Typography>
          </Stack>
        </Stack>
      </TableCell>
      <TableCell>
        <Stack alignItems="flex-end">
          <Stack direction="row" gap={1}>
            <TokenAvatar tokenId={quoteToken.id} size="medium" />
            <Typography
              variant="caption"
              fontWeight={700}
              fontFamily={NumberFont}
              color="secondary"
            >
              {balanceQuote !== null && (
                <TokenAmountBlock
                  amount={balanceQuote}
                  tokenId={quoteToken.id}
                  hideCopyToClipboard
                  needsParsing
                  // showTicker
                  // color="accent.main"
                />
              )}
              {" - "}
              {priceQuote !== null && <UsdAmountBlock amount={priceQuote} />}
            </Typography>
          </Stack>
          {valueQuote !== null && (
            <Typography variant="body1" fontWeight={700} fontFamily={NumberFont}>
              <Typography variant="inherit" component="span" color="secondary">
                Total:
              </Typography>{" "}
              <UsdAmountBlock amount={valueQuote} />
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <Stack alignItems="flex-end">
          <Stack direction="row" gap={1}>
            <TokenAvatar tokenId={baseToken.id} size="medium" />
            <Typography
              variant="caption"
              fontWeight={700}
              fontFamily={NumberFont}
              color="secondary"
            >
              {balanceBase !== null && (
                <TokenAmountBlock
                  amount={balanceBase}
                  tokenId={baseToken.id}
                  hideCopyToClipboard
                  needsParsing
                  // showTicker
                  // color="accent.main"
                />
              )}
              {" - "}
              {priceBase !== null && <UsdAmountBlock amount={priceBase} />}
            </Typography>
          </Stack>
          {valueBase !== null && (
            <Typography variant="body1" fontWeight={700} fontFamily={NumberFont}>
              <Typography variant="inherit" component="span" color="secondary">
                Total:
              </Typography>{" "}
              <UsdAmountBlock amount={valueBase} />
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell align="right">
        {total !== null ? (
          <Typography variant="body1" fontWeight={700} fontFamily={NumberFont}>
            <UsdAmountBlock amount={total} />
          </Typography>
        ) : (
          <Skeleton width={40} sx={{ display: "inline-block" }} />
        )}
      </TableCell>
    </TableRow>
  )
}

/** Minimal mobile component, reusing the same logic but different layout. */
function PoolRowMobile({
  pool,
  entityId,
  onLpValue,
  index,
}: {
  pool: Pool
  entityId: string
  onLpValue: (value: number) => void
  index: number
}) {
  const lpBalance = useLpBalance(entityId, pool.id)

  const [reserves] = useReserves(pool.id, pool.baseToken, pool.quoteToken)
  const [lpTotalSupply] = useTotalSupply(pool.id)
  const [baseToken] = useTokenInfo(pool.baseToken)
  const [quoteToken] = useTokenInfo(pool.quoteToken)
  const [tokenPrices] = useTokenPrices()

  const isLoading =
    typeof lpBalance !== "bigint" ||
    !reserves ||
    !tokenPrices ||
    !baseToken ||
    !quoteToken ||
    typeof lpTotalSupply !== "bigint"

  const baseTokenValue = useMemo(() => {
    if (isLoading) return null
    if (lpBalance === 0n || lpTotalSupply === 0n) return 0
    const price = tokenPrices[baseToken.id]?.price || 0
    const balance = (reserves[baseToken.id] * lpBalance) / lpTotalSupply
    return (
      price * Number(parseBigIntAsNumber(balance, baseToken.denomination, baseToken.denomination))
    )
  }, [isLoading, tokenPrices, baseToken, reserves, lpBalance, lpTotalSupply])

  const quoteTokenValue = useMemo(() => {
    if (isLoading) return null
    if (lpBalance === 0n || lpTotalSupply === 0n) return 0
    const price = tokenPrices[quoteToken.id]?.price || 0
    const balance = (reserves[quoteToken.id] * lpBalance) / lpTotalSupply
    return (
      price * Number(parseBigIntAsNumber(balance, quoteToken.denomination, quoteToken.denomination))
    )
  }, [isLoading, tokenPrices, quoteToken, reserves, lpBalance, lpTotalSupply])

  const total = useMemo(() => {
    if (isLoading || baseTokenValue === null || quoteTokenValue === null) return null
    const val = baseTokenValue + quoteTokenValue
    onLpValue(val)
    return val
  }, [isLoading, baseTokenValue, quoteTokenValue])

  if (isLoading || !lpBalance || lpBalance === 0n) return null

  const priceQuote = tokenPrices[quoteToken.id]?.price
  const balanceQuote = (reserves[quoteToken.id] * lpBalance) / lpTotalSupply
  const valueQuote = quoteTokenValue

  const priceBase = tokenPrices[baseToken.id]?.price
  const balanceBase = (reserves[baseToken.id] * lpBalance) / lpTotalSupply
  const valueBase = baseTokenValue

  return (
    <Box key={pool.id} sx={{ background: "var(--mui-palette-paperAlt)", mb: 2, p: 2 }}>
      <Stack spacing={1}>
        <Stack direction="row" alignItems="center" gap={1}>
          <TokenAvatar tokenId={quoteToken.id} size="medium" />
          <TokenAvatar tokenId={baseToken.id} size="medium" />
          <Typography variant="body1" fontWeight={700}>
            {quoteToken.ticker}/{baseToken.ticker}
          </Typography>
          <Typography variant="caption">
            <IdBlock
              color="text.secondary"
              label={truncateId(pool.id)}
              value={pool.id}
              href={`/pool/${pool.id}`}
            />
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent={"space-between"}>
            <Stack direction="row" alignItems="center" gap={1}>
              {/* <TokenAvatar tokenId={quoteToken.id} /> */}
              <IdBlock
                label={quoteToken.ticker || truncateId(quoteToken.id)}
                value={quoteToken.id}
                href={`/token/${quoteToken.id}`}
              />
            </Stack>

            {valueQuote !== null && (
              <Typography variant="body2">
                Value: <UsdAmountBlock amount={valueQuote} />
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" justifyContent={"space-between"}>
            <Typography variant="body2" color="text.secondary">
              Balance:{" "}
              <TokenAmountBlock
                amount={balanceQuote}
                tokenId={quoteToken.id}
                hideCopyToClipboard
                needsParsing
              />
            </Typography>
            {priceQuote !== undefined && (
              <Typography variant="body2" color="text.secondary">
                Price: <UsdAmountBlock amount={priceQuote} />
              </Typography>
            )}
          </Stack>
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent={"space-between"}>
            <Stack direction="row" alignItems="center" gap={1}>
              {/* <TokenAvatar tokenId={baseToken.id} /> */}
              <IdBlock
                label={baseToken.ticker || truncateId(baseToken.id)}
                value={baseToken.id}
                href={`/token/${baseToken.id}`}
              />
            </Stack>

            {valueBase !== null && (
              <Typography variant="body2">
                Value: <UsdAmountBlock amount={valueBase} />
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center" justifyContent={"space-between"}>
            <Typography variant="body2" color="text.secondary">
              Balance:{" "}
              <TokenAmountBlock
                amount={balanceBase}
                tokenId={baseToken.id}
                hideCopyToClipboard
                needsParsing
              />
            </Typography>
            {priceBase !== undefined && (
              <Typography variant="body2" color="text.secondary">
                Price: <UsdAmountBlock amount={priceBase} />
              </Typography>
            )}
          </Stack>
        </Stack>

        {total !== null && (
          <Typography
            variant="body1"
            textAlign="right"
            fontWeight={700}
            fontFamily={NumberFont}
            mt={1}
          >
            Total: <UsdAmountBlock amount={total} />
          </Typography>
        )}
      </Stack>
    </Box>
  )
}
